<template>
  <div class="w-full flex flex-col gap-4">
    <div class="w-full flex flex-wrap items-center justify-between gap-4">
      <div class="flex flex-wrap items-center gap-4">
        <div class="flex-grow min-w-[350px] sm:flex-grow-0 sm:w-2/5">
          <SearchInput
            v-model="searchInput"
            width="100%"
            label="Buscar pelo nome, CRM ou CRMUF"
            @input="changeSearchInput"
            density="compact"
            hide-details
          />
        </div>
        <div ref="filtersToolbarRef" class="flex flex-wrap gap-2">
          <SelectFilter
            label="CRMUF"
            :icon="ufIcon"
            :options="crmUfOptions"
            :is-open="activeFilter === 'crmUf'"
            dropdown-width="w-56"
            @select="handleCrmUfSelect"
            @click="toggleFilter('crmUf')"
          />
          <SelectFilter
            label="Empresa"
            :icon="companyIcon"
            :options="companyOptions"
            :is-open="activeFilter === 'company'"
            searchable
            dropdown-width="w-64"
            @select="handleCompanySelect"
            @click="toggleFilter('company')"
          />
        </div>
      </div>

      <div class="flex-shrink-0">
        <FilledButton
          @click="() => (createInviteModalProps = true)"
          text="Criar convite"
          width="160px"
          height="40px"
          class="!bg-primary30"
          :disabled="!canCreateInvite"
        />
      </div>
    </div>

    <div v-if="selectedCrmUfLabel || selectedCompanyLabel" class="my-2 flex flex-wrap gap-2">
      <span
        v-if="selectedCrmUfLabel"
        class="inline-flex items-center rounded-full border border-primary30 px-4 py-2 text-sm text-primary30 bg-primary-50"
      >
        CRMUF: {{ selectedCrmUfLabel }}
        <button
          @click="clearCrmUfFilter"
          class="ml-2 text-primary-500 hover:text-primary-700 font-bold focus:outline-none"
        >
          x
        </button>
      </span>
      <span
        v-if="selectedCompanyLabel"
        class="inline-flex items-center rounded-full border border-primary30 px-4 py-2 text-sm text-primary30 bg-primary-50"
      >
        Empresa: {{ selectedCompanyLabel }}
        <button
          @click="clearCompanyFilter"
          class="ml-2 text-primary-500 hover:text-primary-700 font-bold focus:outline-none"
        >
          x
        </button>
      </span>
      <button
        @click="clearAllFilters"
        class="text-sm font-bold text-primary hover:text-primary-darken-1 transition-colors"
      >
        Limpar filtros
      </button>
    </div>

    <div v-if="doctorStore.isError" class="text-center text-red-500 my-4">
      Erro ao carregar dados: {{ doctorStore.errorMessage }}
    </div>

    <Tabs v-model="activeTab" :tabs="tabList">
      <template #ACCEPTED>
        <div class="flex flex-col gap-10 sm:min-h-[570px] justify-between">
          <CardsTab
            :companyDoctors="doctorStore.doctorsCompany || []"
            :isLoading="doctorStore.isLoading"
          />

          <v-pagination
            v-if="!doctorStore.isLoading && (doctorStore.doctorsCompany?.length || 0) > 0"
            v-model="currentPageTab1"
            :total-visible="itemsPerPageTab1"
            :length="pageCountTab1"
            density="comfortable"
            color="primary"
            @update:model-value="changePageTab1"
          />
        </div>
      </template>

      <template #PENDING>
        <div class="flex flex-col gap-10 sm:min-h-[570px] justify-between">
          <OutstandingDoctors
            :companyDoctors="doctorStore.doctorsCompany || []"
            :isLoading="doctorStore.isLoading"
          />
          <div class="flex flex-col gap-4">
            <v-pagination
              v-if="!doctorStore.isLoading && (doctorStore.doctorsCompany?.length || 0) > 0"
              v-model="currentPageTab1"
              :total-visible="itemsPerPageTab1"
              :length="pageCountTab1"
              density="comfortable"
              color="primary"
              @update:model-value="changePageTab1"
            />
            <div class="w-full md:max-w-[120px] inline md:hidden">
              <FilledButton
                @click="() => (createInviteModalProps = true)"
                text="Criar convite"
                width="160px"
                height="40px"
                class="!bg-primary30"
              />
            </div>
          </div>
        </div>
      </template>
    </Tabs>
  </div>

  <CreateInviteModal
    :modalStateProp="createInviteModalProps"
    :updateListFunction="doctorStore.fetchDoctors"
    @close="closeCreateInviteModal"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, defineAsyncComponent } from 'vue'
import { useDoctorStore } from '@/pinia-store/useDoctorStore'
import { useCompanyStore } from '@/pinia-store/useCompanyStore'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { CompanyDoctorInviteStatusEnum } from '@/Network/Types/Requests/CompanyRequests'
import StatesFromBR from '@/config/StatesFromBR'

import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import Tabs, { type TabItem } from '@/components/designSystem/Tabs/Tabs.vue'
import { CreateInviteModal } from './components/atoms'
import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import SelectFilter from '@/components/Filters/SelectFilter.vue'
import { OutstandingDoctors, CardsTab } from '@/modules/company/Doctors/components/organisms'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'

const DOCTORS_PAGE_LIMIT = 12

const ufIcon = defineAsyncComponent(() => import('@/assets/icons/position-icon.vue'))
const companyIcon = defineAsyncComponent(() => import('@/assets/icons/hospital.vue'))

const doctorStore = useDoctorStore()
const companyStore = useCompanyStore()
const { listCompanies } = storeToRefs(companyStore)
const permissionsStore = usePermissionsStore()
const headerStore = useHeaderStore()

const route = useRoute()

const tabList: TabItem[] = [
  {
    label: 'Meus médicos',
    value: CompanyDoctorInviteStatusEnum.ACCEPTED,
    testId: 'my-doctors-tab-button'
  },
  {
    label: 'Médicos pendentes',
    value: CompanyDoctorInviteStatusEnum.PENDING,
    testId: 'pending-doctors-tab-button'
  }
]

const initialInviteStatus =
  (route.query.inviteStatus as CompanyDoctorInviteStatusEnum) ||
  CompanyDoctorInviteStatusEnum.ACCEPTED
const activeTab = ref<string>(initialInviteStatus)

const currentPageTab1 = ref(1)
const itemsPerPageTab1 = ref(15)

const searchInput = ref('')
const createInviteModalProps = ref(false)

const activeFilter = ref<string | null>(null)
const selectedCrmUf = ref<string | null>(null)
const selectedCompanyId = ref<string | null>(null)
const selectedCompanyLabel = ref<string | null>(null)

const crmUfOptions = computed(() =>
  StatesFromBR.map((uf) => ({ label: uf.title, value: uf.value }))
)

const companyOptions = computed(() =>
  listCompanies.value.map((company) => ({
    label: company.title,
    value: String(company.value)
  }))
)

const selectedCrmUfLabel = computed(() => {
  const selected = crmUfOptions.value.find((opt) => opt.value === selectedCrmUf.value)
  return selected ? selected.label : null
})

onMounted(async () => {
  headerStore.setTitle(
    'Médicos',
    'Gerencie médicos vinculados às empresas e acompanhe convites pendentes'
  )
  await doctorStore.applyFilters(
    {
      search: '',
      names: [],
      crms: [],
      crmUFs: [],
      companies: [],
      inviteStatus: activeTab.value as CompanyDoctorInviteStatusEnum,
      limit: DOCTORS_PAGE_LIMIT
    },
    false
  )

  if (listCompanies.value.length === 0) {
    await companyStore.getMyCompanies()
  }
})

const pageCountTab1 = computed(() => {
  if (!doctorStore.doctorQuery.limit) return 1
  return Math.ceil(doctorStore.totalDoctors / doctorStore.doctorQuery.limit)
})

const changePageTab1 = (page: number) => {
  currentPageTab1.value = page
  doctorStore.changePage(page)
}

const closeCreateInviteModal = () => {
  createInviteModalProps.value = false
}

let searchDebounceTimer: number
const changeSearchInput = () => {
  clearTimeout(searchDebounceTimer)
  searchDebounceTimer = window.setTimeout(() => {
    doctorStore.applyFilters({ search: searchInput.value, limit: DOCTORS_PAGE_LIMIT })
    currentPageTab1.value = 1
  }, 500)
}

function toggleFilter(filterName: string) {
  if (activeFilter.value === filterName) {
    activeFilter.value = null
  } else {
    activeFilter.value = filterName
  }
}

const filtersToolbarRef = ref<HTMLElement | null>(null)

// Fecha os dropdowns de filtro ao clicar fora da toolbar. Menus do Vuetify
// são teleportados para o overlay — ignorar.
function handleDocumentClick(event: MouseEvent) {
  const target = event.target as HTMLElement | null
  if (!target || target.closest('.v-overlay-container')) return
  if (filtersToolbarRef.value && !filtersToolbarRef.value.contains(target)) {
    activeFilter.value = null
  }
}

onMounted(() => document.addEventListener('click', handleDocumentClick))
onUnmounted(() => document.removeEventListener('click', handleDocumentClick))

function handleCrmUfSelect(value: string) {
  selectedCrmUf.value = value
  doctorStore.applyFilters({ crmUFs: [value], limit: DOCTORS_PAGE_LIMIT })
  currentPageTab1.value = 1
  activeFilter.value = null
}

function handleCompanySelect(value: string) {
  const selected = companyOptions.value.find((opt) => opt.value === value)
  if (selected) {
    selectedCompanyId.value = selected.value
    selectedCompanyLabel.value = selected.label
    doctorStore.applyFilters({ companies: [selected.value], limit: DOCTORS_PAGE_LIMIT })
    currentPageTab1.value = 1
    activeFilter.value = null
  }
}

function clearCrmUfFilter() {
  selectedCrmUf.value = null
  doctorStore.applyFilters({ crmUFs: [], limit: DOCTORS_PAGE_LIMIT })
  currentPageTab1.value = 1
}

function clearCompanyFilter() {
  selectedCompanyId.value = null
  selectedCompanyLabel.value = null
  doctorStore.applyFilters({ companies: [], limit: DOCTORS_PAGE_LIMIT })
  currentPageTab1.value = 1
}

function clearAllFilters() {
  searchInput.value = ''
  clearCrmUfFilter()
  clearCompanyFilter()
}

watch(activeTab, (newTab) => {
  doctorStore.applyFilters({
    inviteStatus: newTab as CompanyDoctorInviteStatusEnum,
    limit: DOCTORS_PAGE_LIMIT
  })
  currentPageTab1.value = 1
})

const canCreateInvite = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.CompanyDoctorInviteCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})
</script>
